import { useLayoutEffect, useRef, type ReactNode } from 'react';
import { Printer, X } from 'lucide-react';
import { Button } from '@/components/ui/button';

// Landscape A4 with a small margin; the report flows over as many pages as it needs.
const PAGE_STYLE = '@page { size: A4 landscape; margin: 8mm; }';

/**
 * Shrinks the report to the sheet's width (CSS zoom, which keeps page breaks
 * working, unlike a transform), then opens the print dialog once.
 */
function useFitWidthAndPrint(isReady: boolean) {
  const sheetRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (!isReady) return;
    const sheet = sheetRef.current;
    const content = contentRef.current;
    if (!sheet || !content) return;

    const fit = () => {
      content.style.zoom = '1';
      const available = sheet.clientWidth;
      const width = content.scrollWidth;
      if (width > available) content.style.zoom = String(available / width);
    };

    fit();
    // Web fonts can land after the first layout and change the width.
    void document.fonts.ready.then(fit);
    const timer = window.setTimeout(() => window.print(), 400);
    return () => window.clearTimeout(timer);
  }, [isReady]);

  return { sheetRef, contentRef };
}

interface PcmpPrintSheetProps {
  children: ReactNode;
}

/**
 * A loaded report on landscape A4 sheets, with print and close buttons on
 * screen. Mount it only once the report has loaded: it prints on mount.
 */
export function PcmpPrintSheet({ children }: PcmpPrintSheetProps) {
  const { sheetRef, contentRef } = useFitWidthAndPrint(true);

  return (
    <div className="min-h-svh bg-neutral-200 py-6 print:bg-white print:py-0">
      <style>{PAGE_STYLE}</style>
      <div className="mx-auto mb-4 flex w-[297mm] max-w-full justify-end gap-2 px-4 print:hidden">
        <Button type="button" onClick={() => window.print()} className="gap-1.5">
          <Printer className="size-4" />
          Print / Save as PDF
        </Button>
        <Button type="button" variant="outline" onClick={() => window.close()} className="gap-1.5">
          <X className="size-4" />
          Close
        </Button>
      </div>
      <div className="overflow-x-auto print:overflow-visible">
        {/* The paper on screen; in print the page margins take the padding's place. */}
        <div className="mx-auto w-[297mm] bg-white p-[8mm] shadow-lg print:w-auto print:p-0 print:shadow-none">
          {/* The printable width (281mm), the same on screen and on paper. */}
          <div ref={sheetRef} className="w-full">
            <div ref={contentRef}>{children}</div>
          </div>
        </div>
      </div>
    </div>
  );
}
